import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import CircularProgress from '@mui/material/CircularProgress';
import { t } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage, faFileAlt, faVideo, faFile, faUpload, faTrashCan, faXmark,
  faFolder, faFolderOpen, faChevronRight, faPlus, faList, faTh,
  faPencil, faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { DiskFile } from './FilePicker';
import TabMenu from '../util/TabMenu';
import DiskStorageView, { getModuleCfg } from './DiskStorageView';
import NoAccess from '../util/NoAccess';

// ── Types ────────────────────────────────────────────────────────────────────

type FileFilter = 'all' | 'image' | 'document' | 'video';
type ViewMode   = 'grid' | 'list';

interface DiskFolder {
  id: number;
  name: string;
  parentId: number | null;
  module: string;
  createdAt: string;
}

interface BreadcrumbItem { id: number; name: string; }

// ── Helpers ──────────────────────────────────────────────────────────────────

const FILTER_MIME: Record<FileFilter, string> = {
  all: '', image: 'image', document: 'application', video: 'video',
};

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return faImage;
  if (mimeType.startsWith('video/')) return faVideo;
  if (mimeType.includes('pdf') || mimeType.startsWith('application/')) return faFileAlt;
  return faFile;
}

function formatSize(bytes: number | string) {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface DiskPageProps { access: any; baseUrl: string; embedded?: boolean; }

const DiskPage: React.FC<DiskPageProps> = ({ access, baseUrl, embedded = false }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const isAdmin = access?.disk_admin === true || access?.disk_admin === 1;

  const [tab, setTab] = useState('storage');

  // Storage: false = overview (DiskStorageView), true = file browser
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [currentModule, setCurrentModule] = useState<string | null>(null);

  // Navigation
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const currentFolderId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : null;

  // Data
  const [folders, setFolders] = useState<DiskFolder[]>([]);
  const [files, setFiles] = useState<DiskFile[]>([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [view, setView] = useState<ViewMode>('grid');
  const [filter, setFilter] = useState<FileFilter>('all');
  const [selected, setSelected] = useState<DiskFile | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<DiskFolder | null>(null);

  // Create folder
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  // Rename folder inline
  const [renamingFolderId, setRenamingFolderId] = useState<number | null>(null);
  const [renamingFolderName, setRenamingFolderName] = useState('');

  // Rename file
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editingFileName, setEditingFileName] = useState('');

  // Upload
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const folderParams: any = {};
      if (currentFolderId !== null) {
        folderParams.parentId = currentFolderId;
      }

      const fileParams: any = {};
      fileParams.folderId = currentFolderId !== null ? String(currentFolderId) : 'root';
      if (currentModule) fileParams.module = currentModule;
      if (FILTER_MIME[filter]) fileParams.type = FILTER_MIME[filter];

      const [fRes, fiRes] = await Promise.all([
        axios.get<DiskFolder[]>('disk/folders', { params: folderParams }),
        axios.get<DiskFile[]>('disk/files', { params: fileParams }),
      ]);
      setFolders(fRes.data);
      setFiles(fiRes.data);
      setSelected(null);
      setSelectedFolder(null);
    } catch {}
    finally { setLoading(false); }
  }, [currentFolderId, filter, currentModule]);

  useEffect(() => {
    if (auth && tab === 'storage' && showFileBrowser) fetchData();
  }, [auth, fetchData, tab, showFileBrowser]);

  // Focus new folder input when shown
  useEffect(() => {
    if (creatingFolder) newFolderInputRef.current?.focus();
  }, [creatingFolder]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const openFolder = (folder: DiskFolder) => {
    setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    setFilter('all');
  };

  const navigateTo = (index: number) => {
    // index < 0 → root
    setBreadcrumb(prev => index < 0 ? [] : prev.slice(0, index + 1));
    setFilter('all');
  };

  // ── Folder actions ─────────────────────────────────────────────────────────

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setCreatingFolder(false); setNewFolderName(''); return; }
    try {
      const { data } = await axios.post<DiskFolder>('disk/folder', {
        name,
        parentId: currentFolderId ?? undefined,
      });
      setFolders(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch {}
    setCreatingFolder(false);
    setNewFolderName('');
  };

  const handleRenameFolder = async (id: number) => {
    const name = renamingFolderName.trim();
    if (!name) { setRenamingFolderId(null); return; }
    try {
      await axios.patch(`disk/folder/${id}`, { name });
      setFolders(prev => prev.map(f => f.id === id ? { ...f, name } : f));
      if (selectedFolder?.id === id) setSelectedFolder(sf => sf ? { ...sf, name } : null);
    } catch {}
    setRenamingFolderId(null);
  };

  const handleDeleteFolder = async (id: number) => {
    if (!window.confirm(t('DiskDeleteFile') + '?')) return;
    try {
      await axios.delete(`disk/folder/${id}`);
      setFolders(prev => prev.filter(f => f.id !== id));
      if (selectedFolder?.id === id) setSelectedFolder(null);
    } catch {}
  };

  // ── File actions ───────────────────────────────────────────────────────────

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params: any = { module: 'disk' };
      if (currentFolderId !== null) params.folderId = currentFolderId;
      const { data } = await axios.post<DiskFile>('disk/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      });
      setFiles(prev => [data, ...prev]);
      setSelected(data);
    } catch {}
    finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const handleRenameFile = async () => {
    if (!selected || !editingFileId) return;
    const name = editingFileName.trim();
    if (!name || name === selected.originalName) { setEditingFileId(null); return; }
    try {
      await axios.patch(`disk/file/${editingFileId}`, { originalName: name });
      const updated = { ...selected, originalName: name };
      setFiles(prev => prev.map(f => f.id === editingFileId ? updated : f));
      setSelected(updated);
    } catch {}
    setEditingFileId(null);
  };

  const handleDeleteFile = async () => {
    if (!selected || !window.confirm(t('DiskDeleteFile') + '?')) return;
    try {
      await axios.delete(`disk/file/${selected.id}`);
      setFiles(prev => prev.filter(f => f.id !== selected.id));
      setSelected(null);
    } catch {}
  };

  // ── Filtered files ─────────────────────────────────────────────────────────

  const displayedFiles = filter === 'all'
    ? files
    : files.filter(f => f.mimeType.startsWith(FILTER_MIME[filter]));

  const FILTERS: { key: FileFilter; label: string }[] = [
    { key: 'all',      label: t('DiskAllFiles') },
    { key: 'image',    label: t('DiskImages') },
    { key: 'document', label: t('DiskDocuments') },
    { key: 'video',    label: t('DiskVideos') },
  ];

  // ── Shared styles ──────────────────────────────────────────────────────────

  const iconBtn = (active?: boolean): React.CSSProperties => ({
    width: 28, height: 28, borderRadius: 5, display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', fontSize: 12, border: '1px solid var(--border-input)',
    background: active ? 'var(--btn-user-btn)' : 'transparent',
    color: active ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
    transition: 'all .15s',
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  const content = (
    <>
      {/* Overview mode → DiskStorageView */}
      {!showFileBrowser && (
        <DiskStorageView
          onOpenModule={(module) => {
            setBreadcrumb([]);
            setFilter('all');
            setCurrentModule(module);
            setShowFileBrowser(true);
          }}
          onRefreshFiles={() => {
            if (showFileBrowser) fetchData();
          }}
        />
      )}

      {/* File browser mode */}
      {showFileBrowser && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selected ? `1fr ${embedded ? '200px' : '260px'}` : '1fr',
          height: embedded ? '100%' : 'calc(94svh - 60px)',
          overflow: 'hidden',
          borderRadius: 5,
          border: '1px solid var(--border-input)',
          marginTop: embedded ? 0 : 10,
        }}>

          {/* Main area */}
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-form)' }}>

            {/* ── TOOLBAR ── */}
            <div style={{
              padding: embedded ? '7px 10px' : '10px 14px',
              borderBottom: '1px solid var(--border-input)',
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              flexWrap: embedded ? 'nowrap' : 'wrap',
            }}>
              {/* Navigation: back + breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {/* Back to Storage overview */}
                <button type="button"
                  onClick={() => { navigateTo(-1); setShowFileBrowser(false); setCurrentModule(null); }}
                  title="Storage"
                  style={{ ...iconBtn(), flexShrink: 0 }}>
                  <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 10 }} />
                </button>

                {/* Back inside folder */}
                {breadcrumb.length > 0 && (
                  <button type="button" onClick={() => navigateTo(breadcrumb.length - 2)} title="Up"
                    style={{ ...iconBtn(), flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: 11 }} />
                  </button>
                )}

                {/* Breadcrumb */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, overflow: 'hidden' }}>
                  {breadcrumb.length === 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color)', paddingLeft: 2, whiteSpace: 'nowrap' }}>
                      {currentModule ? getModuleCfg(currentModule).label : t('DiskStorage')}
                    </span>
                  )}
                  {breadcrumb.map((item, i) => (
                    <React.Fragment key={item.id}>
                      <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 8, opacity: 0.35, flexShrink: 0 }} />
                      <button type="button" onClick={() => navigateTo(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: i === breadcrumb.length - 1 ? 'var(--text-color)' : 'var(--text-color-calendar)', padding: '1px 3px', borderRadius: 4, fontFamily: 'inherit', fontWeight: i === breadcrumb.length - 1 ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
                        {item.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Filter — compact select when embedded */}
              {embedded ? (
                <select
                  title="Filter"
                  value={filter}
                  onChange={e => setFilter(e.target.value as FileFilter)}
                  style={{ fontSize: 11, padding: '3px 5px', borderRadius: 5, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
                >
                  {FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              ) : (
                <div style={{ display: 'flex', gap: 4 }}>
                  {FILTERS.map(f => (
                    <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                      style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit',
                        border: filter === f.key ? '1px solid #7b6fff' : '1px solid var(--border-input)',
                        background: filter === f.key ? '#7b6fff' : 'transparent',
                        color: filter === f.key ? '#fff' : 'var(--text-color)',
                        opacity: filter === f.key ? 1 : 0.6, transition: 'all .15s',
                      }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}

              {/* View toggle */}
              <div style={{ display: 'flex', gap: 2 }}>
                <button type="button" title="Grid" onClick={() => setView('grid')} style={iconBtn(view === 'grid')}>
                  <FontAwesomeIcon icon={faTh} />
                </button>
                <button type="button" title="List" onClick={() => setView('list')} style={iconBtn(view === 'list')}>
                  <FontAwesomeIcon icon={faList} />
                </button>
              </div>

              {/* New folder — icon only when embedded */}
              <button type="button" onClick={() => { setCreatingFolder(true); setNewFolderName(''); }}
                title={t('DiskNewFolder') || 'New Folder'}
                style={{ ...iconBtn(), background: 'var(--btn-user-btn)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--btn-user-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--btn-user-btn)')}>
                <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />
              </button>

              {/* Upload — icon only when embedded */}
              <button type="button" onClick={() => uploadRef.current?.click()}
                title={t('DiskUpload')}
                style={{ ...iconBtn(), background: '#7b6fff', color: '#fff', border: 'none' }}>
                {uploading ? <CircularProgress size={10} style={{ color: '#fff' }} /> : <FontAwesomeIcon icon={faUpload} style={{ fontSize: 11 }} />}
              </button>
              <input ref={uploadRef} type="file" title={t('DiskUpload')} style={{ display: 'none' }} onChange={handleUpload} />
            </div>

            {/* ── CONTENT ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
                  <CircularProgress size={26} />
                </div>
              ) : (
                <>
                  {/* ── CREATE FOLDER INPUT ── */}
                  {creatingFolder && (
                    <div style={{ marginBottom: 10 }}>
                      {view === 'grid' ? (
                        <div style={{ width: 130, background: 'var(--btn-user-form)', border: '2px solid #7b6fff', borderRadius: 8, padding: '8px 10px', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                          <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: 28, color: '#7b6fff' }} />
                          <input
                            ref={newFolderInputRef}
                            value={newFolderName}
                            onChange={e => setNewFolderName(e.target.value)}
                            onBlur={handleCreateFolder}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                            placeholder={t('DiskFolderName') || 'Folder name'}
                            style={{ width: '100%', fontSize: 12, padding: '3px 5px', borderRadius: 4, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontFamily: 'inherit', textAlign: 'center', outline: 'none' }}
                          />
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border-input)', background: 'rgba(123,111,255,.04)' }}>
                          <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: 14, color: '#7b6fff', width: 18 }} />
                          <input
                            ref={newFolderInputRef}
                            value={newFolderName}
                            onChange={e => setNewFolderName(e.target.value)}
                            onBlur={handleCreateFolder}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                            placeholder={t('DiskFolderName') || 'Folder name'}
                            style={{ flex: 1, fontSize: 13, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontFamily: 'inherit', outline: 'none' }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── GRID VIEW ── */}
                  {view === 'grid' && (
                    <>
                      {/* Folders row */}
                      {folders.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-calendar-week)', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid var(--border-input)' }}>
                            {t('DiskFolders') || 'Folders'} · {folders.length}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                            {folders.map(folder => (
                              <FolderCard
                                key={folder.id}
                                folder={folder}
                                isSelected={selectedFolder?.id === folder.id}
                                isRenaming={renamingFolderId === folder.id}
                                renamingName={renamingFolderName}
                                onRenameNameChange={setRenamingFolderName}
                                onOpen={() => { setSelectedFolder(null); openFolder(folder); }}
                                onSelect={() => { setSelected(null); setSelectedFolder(f => f?.id === folder.id ? null : folder); }}
                                onRenameStart={() => { setRenamingFolderId(folder.id); setRenamingFolderName(folder.name); }}
                                onRenameCommit={() => handleRenameFolder(folder.id)}
                                onRenameCancel={() => setRenamingFolderId(null)}
                                onDelete={() => handleDeleteFolder(folder.id)}
                                isAdmin={isAdmin}
                                view="grid"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Files grid */}
                      {displayedFiles.length > 0 && (
                        <div>
                          {folders.length > 0 && (
                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-calendar-week)', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid var(--border-input)' }}>
                              {t('DiskAllFiles')} · {displayedFiles.length}
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                            {displayedFiles.map(file => (
                              <FileCard
                                key={file.id}
                                file={file}
                                isSelected={selected?.id === file.id}
                                baseUrl={baseUrl}
                                onClick={() => { setSelectedFolder(null); setSelected(f => f?.id === file.id ? null : file); }}
                                view="grid"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {folders.length === 0 && displayedFiles.length === 0 && (
                        <EmptyState />
                      )}
                    </>
                  )}

                  {/* ── LIST VIEW ── */}
                  {view === 'list' && (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Имя', 'Тип', 'Размер', 'Дата'].map(h => (
                            <th key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--border-input)', background: 'var(--background-color)', whiteSpace: 'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {folders.map(folder => (
                          <FolderCard
                            key={folder.id}
                            folder={folder}
                            isSelected={selectedFolder?.id === folder.id}
                            isRenaming={renamingFolderId === folder.id}
                            renamingName={renamingFolderName}
                            onRenameNameChange={setRenamingFolderName}
                            onOpen={() => { setSelectedFolder(null); openFolder(folder); }}
                            onSelect={() => { setSelected(null); setSelectedFolder(f => f?.id === folder.id ? null : folder); }}
                            onRenameStart={() => { setRenamingFolderId(folder.id); setRenamingFolderName(folder.name); }}
                            onRenameCommit={() => handleRenameFolder(folder.id)}
                            onRenameCancel={() => setRenamingFolderId(null)}
                            onDelete={() => handleDeleteFolder(folder.id)}
                            isAdmin={isAdmin}
                            view="list"
                          />
                        ))}
                        {displayedFiles.map(file => (
                          <FileCard
                            key={file.id}
                            file={file}
                            isSelected={selected?.id === file.id}
                            baseUrl={baseUrl}
                            onClick={() => { setSelectedFolder(null); setSelected(f => f?.id === file.id ? null : file); }}
                            view="list"
                          />
                        ))}
                        {folders.length === 0 && displayedFiles.length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '40px', opacity: 0.35, fontSize: 13 }}>{t('Empty')}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── SIDE PANEL — file details ── */}
          {selected && (
            <SidePanel
              file={selected}
              baseUrl={baseUrl}
              isAdmin={isAdmin}
              editingFileId={editingFileId}
              editingFileName={editingFileName}
              onEditingFileNameChange={setEditingFileName}
              onRenameStart={() => { setEditingFileId(selected.id); setEditingFileName(selected.originalName); }}
              onRenameCommit={handleRenameFile}
              onRenameCancel={() => setEditingFileId(null)}
              onDelete={handleDeleteFile}
              onClose={() => setSelected(null)}
            />
          )}

          {/* ── SIDE PANEL — folder details ── */}
          {selectedFolder && !selected && (
            <FolderSidePanel
              folder={selectedFolder}
              isAdmin={isAdmin}
              onOpen={() => openFolder(selectedFolder)}
              onDelete={() => handleDeleteFolder(selectedFolder.id)}
              onClose={() => setSelectedFolder(null)}
            />
          )}
        </div>
      )}
    </>
  );

  // Раздел закрыт ролью — показываем отказ, а не пустое хранилище
  const diskAllowed = access?.disk_enabled === true || access?.disk_enabled === 1
    || access?.disk_view === true || access?.disk_view === 1;
  if (access && !diskAllowed && !embedded) {
    return <NoAccess section={t('Disk')} />;
  }

  if (embedded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {content}
      </div>
    );
  }

  return (
    <TabMenu
      value={tab}
      onChange={(v: string) => setTab(v)}
      tabs={[{ label: t('DiskStorage'), value: 'storage' }]}
    >
      {content}
    </TabMenu>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════════

function EmptyState() {
  return (
    <div style={{ textAlign: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.15 }}>📂</div>
      <div style={{ fontSize: 13, opacity: 0.35 }}>Папка пуста</div>
    </div>
  );
}

// ── Folder card (grid + list) ──────────────────────────────────────────────────

interface FolderCardProps {
  folder: DiskFolder;
  isSelected: boolean;
  isRenaming: boolean;
  renamingName: string;
  onRenameNameChange: (v: string) => void;
  onOpen: () => void;
  onSelect: () => void;
  onRenameStart: () => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  onDelete: () => void;
  isAdmin: boolean;
  view: 'grid' | 'list';
}

function FolderCard({
  folder, isSelected, isRenaming, renamingName, onRenameNameChange,
  onOpen, onSelect, onRenameStart, onRenameCommit, onRenameCancel,
  onDelete, isAdmin, view,
}: FolderCardProps) {
  if (view === 'list') {
    return (
      <tr
        style={{ borderBottom: '1px solid var(--border-input)', background: isSelected ? 'var(--bg-top)' : 'transparent', transition: 'background .1s', cursor: 'pointer' }}
        onClick={onSelect}
        onDoubleClick={onOpen}
      >
        <td style={{ padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={isSelected ? faFolderOpen : faFolder} style={{ fontSize: 14, color: '#e8a000', width: 16, flexShrink: 0 }} />
            {isRenaming ? (
              <input
                autoFocus
                title="Folder name"
                placeholder="Folder name"
                value={renamingName}
                onChange={e => onRenameNameChange(e.target.value)}
                onBlur={onRenameCommit}
                onKeyDown={e => { if (e.key === 'Enter') onRenameCommit(); if (e.key === 'Escape') onRenameCancel(); }}
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 13, padding: '1px 5px', borderRadius: 4, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontFamily: 'inherit', outline: 'none' }}
              />
            ) : (
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)' }}>{folder.name}</span>
            )}
          </div>
        </td>
        <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>Folder</td>
        <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>—</td>
        <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {new Date(folder.createdAt).toLocaleDateString()}
            <span style={{ marginLeft: 6, display: 'flex', gap: 3 }}>
              <button type="button" title="Rename" onClick={e => { e.stopPropagation(); onRenameStart(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--text-color-calendar-week)', padding: '2px 4px', borderRadius: 3 }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-color)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-color-calendar-week)')}>
                <FontAwesomeIcon icon={faPencil} />
              </button>
              {isAdmin && (
                <button type="button" title="Delete" onClick={e => { e.stopPropagation(); onDelete(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--text-color-calendar-week)', padding: '2px 4px', borderRadius: 3 }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-color-calendar-week)')}>
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              )}
            </span>
          </div>
        </td>
      </tr>
    );
  }

  // Grid
  return (
    <div
      onClick={onSelect}
      onDoubleClick={onOpen}
      style={{
        borderRadius: 8, cursor: 'pointer', padding: '10px 10px 8px',
        border: isSelected ? '2px solid #7b6fff' : '2px solid transparent',
        background: isSelected ? 'rgba(123,111,255,.06)' : 'var(--btn-user-form)',
        transition: 'border .15s, background .15s',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
        position: 'relative',
      }}
    >
      <FontAwesomeIcon icon={isSelected ? faFolderOpen : faFolder} style={{ fontSize: 30, color: '#e8a000' }} />
      {isRenaming ? (
        <input
          autoFocus
          title="Folder name"
          placeholder="Folder name"
          value={renamingName}
          onChange={e => onRenameNameChange(e.target.value)}
          onBlur={onRenameCommit}
          onKeyDown={e => { if (e.key === 'Enter') onRenameCommit(); if (e.key === 'Escape') onRenameCancel(); }}
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontFamily: 'inherit', outline: 'none', textAlign: 'center' }}
        />
      ) : (
        <div style={{ fontSize: 11, fontWeight: 500, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.3, color: 'var(--text-color)' }}>
          {folder.name}
        </div>
      )}
      {/* Actions on hover */}
      {isSelected && (
        <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
          <button type="button" title="Rename" onClick={onRenameStart}
            style={{ width: 18, height: 18, borderRadius: 3, background: 'var(--btn-user-in)', border: 'none', cursor: 'pointer', fontSize: 9, color: 'var(--text-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faPencil} />
          </button>
          {isAdmin && (
            <button type="button" title="Delete" onClick={onDelete}
              style={{ width: 18, height: 18, borderRadius: 3, background: 'rgba(239,68,68,.1)', border: 'none', cursor: 'pointer', fontSize: 9, color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── File card (grid + list) ────────────────────────────────────────────────────

interface FileCardProps {
  file: DiskFile;
  isSelected: boolean;
  baseUrl: string;
  onClick: () => void;
  view: 'grid' | 'list';
}

function FileCard({ file, isSelected, baseUrl, onClick, view }: FileCardProps) {
  const isImg = file.mimeType.startsWith('image/');
  const imgSrc = `${baseUrl}${file.url.replace(/^\//, '')}`;

  if (view === 'list') {
    return (
      <tr
        onClick={onClick}
        style={{ borderBottom: '1px solid var(--border-input)', background: isSelected ? 'var(--bg-top)' : 'transparent', transition: 'background .1s', cursor: 'pointer' }}
        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--btn-user-form)'; }}
        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      >
        <td style={{ padding: '7px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isImg ? (
              <img src={imgSrc} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <FontAwesomeIcon icon={fileIcon(file.mimeType)} style={{ fontSize: 14, opacity: 0.4, width: 24, flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 13, color: 'var(--text-color)', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
              {file.originalName}
            </span>
          </div>
        </td>
        <td style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
          {file.mimeType.split('/')[0]}
        </td>
        <td style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-color-calendar)', whiteSpace: 'nowrap' }}>
          {formatSize(file.size)}
        </td>
        <td style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap' }}>
          {new Date(file.createdAt).toLocaleDateString()}
        </td>
      </tr>
    );
  }

  // Grid
  return (
    <div
      onClick={onClick}
      style={{
        borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
        border: isSelected ? '2px solid #7b6fff' : '2px solid transparent',
        background: 'var(--btn-user-form)', transition: 'border .15s',
      }}
    >
      {isImg ? (
        <div style={{ height: 110, overflow: 'hidden', background: 'var(--bg-form)' }}>
          <img src={imgSrc} alt={file.originalName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      ) : (
        <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-form)' }}>
          <FontAwesomeIcon icon={fileIcon(file.mimeType)} style={{ fontSize: 36, opacity: 0.22 }} />
        </div>
      )}
      <div style={{ padding: '6px 8px' }}>
        <div style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {file.originalName}
        </div>
        <div style={{ fontSize: 10, opacity: 0.4, marginTop: 1 }}>{formatSize(file.size)}</div>
      </div>
    </div>
  );
}

// ── File side panel ────────────────────────────────────────────────────────────

interface SidePanelProps {
  file: DiskFile;
  baseUrl: string;
  isAdmin: boolean;
  editingFileId: number | null;
  editingFileName: string;
  onEditingFileNameChange: (v: string) => void;
  onRenameStart: () => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function SidePanel({ file, baseUrl, isAdmin, editingFileId, editingFileName, onEditingFileNameChange, onRenameStart, onRenameCommit, onRenameCancel, onDelete, onClose }: SidePanelProps) {
  const imgSrc = `${baseUrl}${file.url.replace(/^\//, '')}`;
  return (
    <div style={{ borderLeft: '1px solid var(--border-input)', background: 'var(--bg-form)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Information')}</div>
        <button type="button" title={t('Close')} onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-color)', opacity: 0.4 }}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {file.mimeType.startsWith('image/') && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-input)' }}>
          <img src={imgSrc} alt={file.originalName} style={{ width: '100%', borderRadius: 6, border: '1px solid var(--border-input)', display: 'block' }} />
        </div>
      )}
      {file.mimeType.startsWith('video/') && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-input)' }}>
          <video src={imgSrc} controls style={{ width: '100%', borderRadius: 6, display: 'block' }} />
        </div>
      )}

      <div style={{ padding: '10px 14px', flex: 1, overflow: 'auto' }}>
        <FieldRow label={t('Title')}>
          {editingFileId === file.id ? (
            <input autoFocus title={t('Title')} placeholder={t('Title')} value={editingFileName}
              onChange={e => onEditingFileNameChange(e.target.value)}
              onBlur={onRenameCommit}
              onKeyDown={e => { if (e.key === 'Enter') onRenameCommit(); if (e.key === 'Escape') onRenameCancel(); }}
              className="input-timesheet"
              style={{ fontSize: 13, marginTop: 4, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', width: '100%', fontFamily: 'inherit' }}
            />
          ) : (
            <span onClick={onRenameStart} title="Click to rename"
              style={{ fontSize: 13, marginTop: 2, wordBreak: 'break-all', cursor: 'text' }}>
              {file.originalName}
            </span>
          )}
        </FieldRow>
        <FieldRow label={t('Size')} value={formatSize(file.size)} />
        <FieldRow label={t('Date')} value={new Date(file.createdAt).toLocaleString()} />
        {file.uploaderName && (
          <FieldRow label={t('AddedBy')} value={file.uploaderName} />
        )}
        {file.module && (
          <FieldRow label={t('Section')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span style={{ fontSize: 13 }}>{getModuleCfg(file.module).icon}</span>
              <span style={{ fontSize: 13, color: 'var(--text-color)', fontWeight: 500 }}>
                {getModuleCfg(file.module).label}
              </span>
            </div>
          </FieldRow>
        )}
        <FieldRow label={t('DirectLink')}>
          <a
            href={imgSrc}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all', marginTop: 2, display: 'block' }}
          >
            {file.url.replace(/^\//, '').split('/').pop()}
          </a>
        </FieldRow>
      </div>

      {isAdmin && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-input)' }}>
          <button type="button" onClick={onDelete}
            style={{ width: '100%', padding: '7px', borderRadius: 5, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--red-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 12 }} />
            {t('DiskDeleteFile')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Folder side panel ──────────────────────────────────────────────────────────

interface FolderSidePanelProps {
  folder: DiskFolder;
  isAdmin: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function FolderSidePanel({ folder, isAdmin, onOpen, onDelete, onClose }: FolderSidePanelProps) {
  return (
    <div style={{ borderLeft: '1px solid var(--border-input)', background: 'var(--bg-form)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Information')}</div>
        <button type="button" title="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-color)', opacity: 0.4 }}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div style={{ padding: '20px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border-input)' }}>
        <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: 48, color: '#e8a000' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-color)', textAlign: 'center' }}>{folder.name}</div>
      </div>

      <div style={{ padding: '10px 14px', flex: 1 }}>
        <FieldRow label={t('Date')} value={new Date(folder.createdAt).toLocaleDateString()} />
      </div>

      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-input)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" onClick={onOpen}
          style={{ width: '100%', padding: '7px', borderRadius: 5, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', background: 'var(--btn-user-btn)', border: 'none', color: 'var(--text-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--btn-user-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--btn-user-btn)')}>
          <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: 12 }} />
          Открыть папку
        </button>
        {isAdmin && (
          <button type="button" onClick={onDelete}
            style={{ width: '100%', padding: '7px', borderRadius: 5, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--red-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 12 }} />
            {t('DiskDeleteFile')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Generic field row ──────────────────────────────────────────────────────────

function FieldRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '6px 0', borderBottom: '1px solid var(--border-input)' }}>
      <span style={{ fontSize: 10, opacity: 0.45, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      {children || <span style={{ fontSize: 13, marginTop: 2, wordBreak: 'break-all' }}>{value}</span>}
    </div>
  );
}

export default DiskPage;
